import { EAELogo } from "@/assets/svg-icons/svg-icons";
import React, { useMemo, useState } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import cn from "@/utils/cn";
import TextComponent from "./TextComponent";

// A placeholder icon, replace with your actual logo/icon if different
const ProductIcon = () => (
  <div className="flex size-[45px] items-center justify-center rounded-[15px] border border-kd-lte-blue xl:size-16 xl:rounded-2xl">
    <EAELogo />
  </div>
);

interface PaymentLayoutProps {
  children: React.ReactNode;
  step: "payment" | "confirmation";
  planName?: string;
  price?: number;
  billingCycle?: "MONTHLY" | "ANNUAL";
  orderNumber?: string;
  activationDate?: string;
  onPromoCodeApplied?: any;
  tax?: number;
  onNavigateToSelection?: () => void;
  appliedCoupon?: any | null;
}

export const PaymentLayout: React.FC<PaymentLayoutProps> = ({
  children,
  step,
  planName = "Standard",
  price = 252.0,
  billingCycle = "MONTHLY",
  orderNumber,
  activationDate,
  onPromoCodeApplied,
  tax = 0,
  onNavigateToSelection,
  appliedCoupon,
}) => {
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const formattedPrice = `$${price.toFixed(2)}`;
  // const billingText = billingCycle === "ANNUAL" ? "/year" : "/month";
  const billingText = "";
  const formattedPriceWithCycle = `${formattedPrice}${billingText}`;
  const handleApplyPromoCode = async () => {
    setPromoLoading(true);
    setPromoError(null);

    try {
      const response = await fetch("/api/promo-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ promoCode }),
      });

      const data = await response.json();

      if (response.ok) {
        onPromoCodeApplied(data.coupon);
      } else {
        setPromoError(data.message || "An error occurred");
      }
    } catch (error) {
      setPromoError("An unexpected error occurred");
    } finally {
      setPromoLoading(false);
    }
  };
  // Format to "Month Day, Year" e.g., "Jun 2, 2024"
  const nextBillingDate = new Date();
  if (billingCycle === "ANNUAL") {
    nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
  } else {
    nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  }

  const autopayDate = nextBillingDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const activationDateFormatted = activationDate
    ? new Date(activationDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.percent_off) {
      return price * (appliedCoupon.percent_off / 100);
    }
    if (appliedCoupon.amount_off) {
      return appliedCoupon.amount_off / 100;
    }
    return 0;
  }, [appliedCoupon, price]);

  return (
    <div className="mt-[6px] grid grid-cols-1 lg:grid-cols-1 xl:grid-cols-2 xl:gap-x-24">
      {/* Left Column: Order Summary */}
      <div className="flex w-full flex-col justify-between font-['Poppins']">
        <div>
          <TextComponent
            type="h3"
            className="mb-[24px] text-[#05004E] xl:mb-[32px]"
          >
            {step === "payment" ? "Activate Email Audit Engine" : ""}
          </TextComponent>

          {/* Navigation Breadcrumbs */}
          <nav className="mb-6">
            <div className="flex items-center">
              <TextComponent
                type="caption"
                className="cursor-pointer text-slate-600"
                onClick={onNavigateToSelection}
              >
                Selection
              </TextComponent>
              <HiOutlineChevronRight className="mx-2 size-3.5 text-slate-600" />
              <TextComponent
                type="caption"
                className={cn(
                  "capitalize",
                  step === "payment"
                    ? "font-semibold text-kd-lte-blue"
                    : "text-slate-600",
                )}
              >
                Payment
              </TextComponent>
              <HiOutlineChevronRight className="mx-2 size-3.5 text-neutral-400" />
              <TextComponent
                type="caption"
                className={cn(
                  "capitalize",
                  step === "confirmation"
                    ? "font-semibold text-kd-lte-blue"
                    : "text-neutral-400",
                )}
              >
                Confirmation
              </TextComponent>
            </div>
          </nav>
        </div>
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 ">
              <ProductIcon />
              <TextComponent type="h4" className="text-[#05004E]">
                Email Audit Engine{" "}
                <span className="text-kd-lte-blue">{planName}</span>
              </TextComponent>
            </div>

            {step === "payment" ? (
              <TextComponent
                type="body"
                className="text-right font-medium text-[#05004E]"
              >
                {formattedPriceWithCycle}
              </TextComponent>
            ) : (
              <TextComponent
                type="body"
                className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 font-medium text-green-600"
              >
                <span className="size-1.5 rounded-full bg-green-500"></span>
                Active
              </TextComponent>
            )}
          </div>

          <hr className="h-1 border-[#EDF2F6]" />

          {step === "payment" ? (
            <>
              <TextComponent
                type="body"
                className="flex justify-between text-kd-gray-text"
              >
                <span className="font-medium ">Subtotal</span>
                <span className="text-right font-medium">
                  {formattedPriceWithCycle}
                </span>
              </TextComponent>
              {!showPromoInput && step === "payment" && !appliedCoupon && (
                <TextComponent type="body" className="text-kd-lte-blue">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPromoInput(true);
                    }}
                    className="hover:underline"
                  >
                    Add promotion code
                  </a>
                </TextComponent>
              )}
              {showPromoInput && step === "payment" && !appliedCoupon && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Enter promo code"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-kd-lte-blue"
                  />
                  <button
                    onClick={handleApplyPromoCode}
                    disabled={promoLoading}
                    className="rounded-md bg-kd-lte-blue px-4 py-2 text-white disabled:opacity-50"
                  >
                    {promoLoading ? "Applying..." : "Apply"}
                  </button>
                </div>
              )}
              {promoError && (
                <p className="mt-2 text-sm text-red-600">{promoError}</p>
              )}
              {appliedCoupon && (
                <TextComponent
                  type="body"
                  className="flex justify-between text-kd-gray-text"
                >
                  <span className="font-medium">
                    Discount ({appliedCoupon.name})
                  </span>
                  <span className="text-right font-medium">
                    -${discountAmount.toFixed(2)}
                  </span>
                </TextComponent>
              )}
              <TextComponent
                type="body"
                className="flex justify-between text-kd-gray-text"
              >
                <span className="font-medium">Tax</span>
                <span className="text-right font-medium">$0.00</span>
              </TextComponent>
            </>
          ) : (
            <>
              <TextComponent
                type="body"
                className="flex justify-between text-kd-gray-text"
              >
                <span className="font-medium">Order number</span>
                <span className="text-right font-medium text-indigo-950">
                  {orderNumber}
                </span>
              </TextComponent>
              <TextComponent
                type="body"
                className="flex justify-between text-[#96A5B8]"
              >
                <span className="text-right font-medium">
                  Activated on {activationDateFormatted}
                </span>
              </TextComponent>
            </>
          )}

          <hr className="h-1 border-[#EDF2F6]" />

          <TextComponent
            type="body"
            className="flex justify-between font-medium text-kd-gray-text"
          >
            <span>{step === "payment" ? "Total due" : "Total paid"}</span>
            <span className="text-right">
              {step === "payment"
                ? `$${(price - discountAmount + tax).toFixed(2)}`
                : formattedPrice}
            </span>
          </TextComponent>

          <TextComponent type="caption" className="text-slate-600">
            {step === "payment"
              ? `Next ${billingCycle === "ANNUAL" ? "annual" : "monthly"} payment on ${autopayDate}`
              : `Next ${billingCycle === "ANNUAL" ? "annual" : "monthly"} payment on ${autopayDate}`}
          </TextComponent>
        </div>
      </div>

      {/* Right Column: Content (Payment Form or Confirmation) */}
      <div className="mt-10 w-full lg:mt-0">{children}</div>
    </div>
  );
};
